import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ORDER_STATUSES } from '@cr/shared';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Field,
  Input,
  Select,
  formatSum,
  toTiyin,
} from '@/components/ui';

/** Подписи статусов — те же формулировки, что видит клиент в Mini App. */
const STATUS_LABELS: Record<string, string> = {
  NEW: 'Новый',
  AWAITING_PAYMENT: 'Ожидает оплаты',
  PAID: 'Оплачен',
  AWAITING_ADMIN_CONFIRMATION: 'Ожидает подтверждения',
  AWAITING_MYID: 'Нужна верификация MyID',
  VERIFIED: 'Верификация пройдена',
  PREPARING: 'Подготавливается',
  READY_FOR_PICKUP: 'Готов к выдаче',
  HANDED_TO_DELIVERY: 'Передан в доставку',
  DELIVERED: 'Доставлен',
  RENTAL_ACTIVE: 'Аренда активна',
  AWAITING_RETURN: 'Ожидает возврата',
  RETURNED: 'Возвращён',
  WAREHOUSE_INSPECTION: 'Проверяется складом',
  DAMAGE_DETECTED: 'Обнаружено повреждение',
  FINE_ISSUED: 'Начислен штраф',
  COMPLETED: 'Завершён',
  CANCELLED: 'Отменён',
};

const PAYMENT_LABELS: Record<string, string> = {
  CLICK: 'Click',
  PAYME: 'Payme',
  CASH: 'Наличными',
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  UNPAID: 'Не оплачен',
  PARTIALLY_PAID: 'Частично',
  PAID: 'Оплачен',
  REFUNDED: 'Возвращён',
};

interface OrderRow {
  id: string;
  number: string;
  status: string;
  paymentStatus: string;
  paymentMethod: string;
  fulfillmentType: string;
  total: number;
  paidAmount: number;
  minStartDate: string;
  maxEndDate: string;
  createdAt: string;
  user: { firstName: string | null; lastName: string | null; phone: string | null };
}

interface OrderDetail extends OrderRow {
  lateFeeTotal: number;
  overdueDays: number;
  depositTotal: number;
  depositMode: string;
  passportHeldAt: string | null;
  passportReturnedAt: string | null;
  comment: string | null;
  addressSnapshot: { addressLine: string } | null;
  items: Array<{
    id: string;
    snapshot: { name?: Record<string, string> };
    qty: number;
    subtotal: number;
  }>;
  history: Array<{ id: string; toStatus: string; comment: string | null; createdAt: string }>;
  availableTransitions: Array<{ to: string; label: string }>;
}

export default function OrdersPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orders = useQuery({
    queryKey: ['admin-orders', status, query],
    queryFn: async () =>
      (
        await api.get<{ items: OrderRow[]; total: number }>('/admin/orders', {
          params: { status: status || undefined, q: query || undefined },
        })
      ).data,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
    if (openId) void queryClient.invalidateQueries({ queryKey: ['admin-order', openId] });
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Заказы</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Статус меняется только по разрешённым переходам — кнопки приходят с сервера
          </p>
        </div>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <JobsPanel onError={setError} onDone={invalidate} />

      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-56">
            <option value="">Все статусы</option>
            {ORDER_STATUSES.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value] ?? value}
              </option>
            ))}
          </Select>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Номер заказа"
            className="w-48"
          />
        </div>

        {orders.isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : (orders.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-neutral-500">Заказов не найдено</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-neutral-500">
                <tr>
                  <th className="py-2">Номер</th>
                  <th>Клиент</th>
                  <th>Период</th>
                  <th>Статус</th>
                  <th>Оплата</th>
                  <th className="text-right">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {orders.data!.items.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => setOpenId(order.id === openId ? null : order.id)}
                    className="cursor-pointer border-t border-neutral-100 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-900"
                  >
                    <td className="py-2 font-medium">{order.number}</td>
                    <td>
                      {[order.user.firstName, order.user.lastName].filter(Boolean).join(' ') || '—'}
                      <span className="block text-xs text-neutral-500">{order.user.phone}</span>
                    </td>
                    <td className="text-xs">
                      {order.minStartDate} — {order.maxEndDate}
                    </td>
                    <td>
                      <Badge tone={order.status === 'CANCELLED' ? 'red' : 'neutral'}>
                        {STATUS_LABELS[order.status] ?? order.status}
                      </Badge>
                    </td>
                    <td className="text-xs">
                      {PAYMENT_LABELS[order.paymentMethod]}
                      <span className="block text-neutral-500">
                        {PAYMENT_STATUS_LABELS[order.paymentStatus]}
                      </span>
                    </td>
                    <td className="text-right font-medium">{formatSum(order.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openId && <OrderCard id={openId} onError={setError} onChanged={invalidate} />}
    </div>
  );
}

function OrderCard({
  id,
  onError,
  onChanged,
}: {
  id: string;
  onError: (message: string | null) => void;
  onChanged: () => void;
}) {
  const [comment, setComment] = useState('');
  const [cashAmount, setCashAmount] = useState('');

  const order = useQuery({
    queryKey: ['admin-order', id],
    queryFn: async () => (await api.get<OrderDetail>(`/admin/orders/${id}`)).data,
  });

  const act = useMutation({
    mutationFn: async (action: { url: string; body?: Record<string, unknown> }) =>
      api.post(action.url, action.body ?? {}),
    onSuccess: () => {
      onError(null);
      setComment('');
      setCashAmount('');
      onChanged();
    },
    onError: (e) => onError(errorMessage(e)),
  });

  if (order.isLoading || !order.data) {
    return (
      <Card title="Заказ">
        <p className="text-sm text-neutral-500">Загрузка…</p>
      </Card>
    );
  }

  const o = order.data;
  const passportHeld = Boolean(o.passportHeldAt && !o.passportReturnedAt);
  const cashUnpaid = o.paymentMethod === 'CASH' && o.paymentStatus !== 'PAID';

  return (
    <Card title={`Заказ ${o.number}`}>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2 text-sm">
          <Line label="Статус" value={STATUS_LABELS[o.status] ?? o.status} />
          <Line
            label="Оплата"
            value={`${PAYMENT_LABELS[o.paymentMethod]} · ${PAYMENT_STATUS_LABELS[o.paymentStatus]}`}
          />
          <Line label="Получено" value={`${formatSum(o.paidAmount)} из ${formatSum(o.total)}`} />
          <Line
            label="Залог"
            value={`${formatSum(o.depositTotal)} · ${o.depositMode === 'CASH_ON_PICKUP' ? 'наличными' : o.depositMode === 'HOLD' ? 'холд' : 'списание'}`}
          />
          {o.addressSnapshot && <Line label="Адрес" value={o.addressSnapshot.addressLine} />}
          {o.comment && <Line label="Комментарий" value={o.comment} />}

          {o.overdueDays > 0 && (
            <Line
              label={`Просрочка, ${o.overdueDays} дн.`}
              value={`пеня ${formatSum(o.lateFeeTotal)}`}
            />
          )}

          {passportHeld && (
            <Badge tone="amber" title="Пока паспорт у нас, заказ не закрыть">
              паспорт на хранении
            </Badge>
          )}
        </div>

        <div className="space-y-3">
          <Field label="Комментарий к переходу">
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Попадёт в историю заказа"
            />
          </Field>

          <div className="flex flex-wrap gap-2">
            {o.availableTransitions.length === 0 && (
              <p className="text-sm text-neutral-500">Доступных переходов нет</p>
            )}
            {o.availableTransitions.map((transition) => (
              <Button
                key={transition.to}
                variant={transition.to === 'CANCELLED' ? 'danger' : 'primary'}
                disabled={act.isPending}
                onClick={() =>
                  act.mutate({
                    url: `/admin/orders/${o.id}/transition`,
                    body: { to: transition.to, comment: comment || undefined },
                  })
                }
              >
                {transition.label}
              </Button>
            ))}
          </div>

          {cashUnpaid && (
            <div className="space-y-2 rounded-lg border border-neutral-200 p-3 dark:border-neutral-800">
              <p className="text-sm font-medium">Приём наличных при выдаче</p>
              <Field label="Сумма, сум" hint={`По умолчанию весь итог — ${formatSum(o.total)}`}>
                <Input
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                  inputMode="decimal"
                  placeholder="Весь итог"
                />
              </Field>
              <div className="flex gap-2">
                <Button
                  disabled={act.isPending}
                  onClick={() =>
                    act.mutate({
                      url: `/admin/payments/orders/${o.id}/cash`,
                      body: {
                        amount: cashAmount ? toTiyin(cashAmount) : undefined,
                        passportHeld: true,
                      },
                    })
                  }
                >
                  Принять деньги и паспорт
                </Button>
              </div>
              <p className="text-xs text-neutral-500">
                Паспорт остаётся на хранении до возврата вещи (Q9) — это фиксируется в журнале
              </p>
            </div>
          )}

          {passportHeld && (
            <Button
              variant="secondary"
              disabled={act.isPending}
              onClick={() => act.mutate({ url: `/admin/orders/${o.id}/passport/return` })}
            >
              Вернуть паспорт клиенту
            </Button>
          )}
        </div>
      </div>

      {o.paymentMethod !== 'CASH' && <MyIdBlock orderId={o.id} />}

      <div className="mt-4 border-t border-neutral-100 pt-3 dark:border-neutral-800">
        <p className="mb-2 text-sm font-medium">История</p>
        <ul className="space-y-1 text-xs text-neutral-500">
          {o.history.map((entry) => (
            <li key={entry.id}>
              {new Date(entry.createdAt).toLocaleString('ru-RU')} —{' '}
              {STATUS_LABELS[entry.toStatus] ?? entry.toStatus}
              {entry.comment && ` · ${entry.comment}`}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-neutral-500">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

interface MyIdSession {
  id: string;
  status: string;
  maskedDocument: string | null;
  fullName: string | null;
  failureReason: string | null;
  attempts: number;
  createdAt: string;
  verifiedAt: string | null;
}

const MYID_LABELS: Record<string, { label: string; tone: 'neutral' | 'green' | 'red' | 'amber' }> =
  {
    PENDING: { label: 'Создана', tone: 'neutral' },
    IN_PROGRESS: { label: 'Клиент проходит', tone: 'amber' },
    VERIFIED: { label: 'Пройдена', tone: 'green' },
    FAILED: { label: 'Отказ', tone: 'red' },
    EXPIRED: { label: 'Истекла', tone: 'neutral' },
  };

/**
 * Попытки верификации. Показываем только маскированные данные — паспорт
 * целиком открывается отдельной кнопкой, и это попадает в журнал аудита.
 */
function MyIdBlock({ orderId }: { orderId: string }) {
  const [payload, setPayload] = useState<Record<string, unknown> | null>(null);
  const [reason, setReason] = useState('');

  const sessions = useQuery({
    queryKey: ['myid', orderId],
    queryFn: async () => (await api.get<MyIdSession[]>(`/admin/myid/orders/${orderId}`)).data,
  });

  const reveal = useMutation({
    mutationFn: async (id: string) =>
      (
        await api.get<Record<string, unknown>>(`/admin/myid/sessions/${id}/payload`, {
          params: { reason },
        })
      ).data,
    onSuccess: setPayload,
  });

  if ((sessions.data?.length ?? 0) === 0) return null;

  return (
    <div className="mt-4 border-t border-neutral-100 pt-3 dark:border-neutral-800">
      <p className="mb-2 text-sm font-medium">Верификация MyID</p>

      <ul className="space-y-2">
        {sessions.data!.map((session) => (
          <li key={session.id} className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={MYID_LABELS[session.status]?.tone ?? 'neutral'}>
              {MYID_LABELS[session.status]?.label ?? session.status}
            </Badge>
            <span className="text-neutral-500">попытка {session.attempts}</span>
            {session.fullName && <span>{session.fullName}</span>}
            {session.maskedDocument && (
              <span className="font-mono text-xs">{session.maskedDocument}</span>
            )}
            {session.failureReason && (
              <span className="text-xs text-red-500">{session.failureReason}</span>
            )}
            {session.status === 'VERIFIED' && (
              <Button variant="ghost" onClick={() => reveal.mutate(session.id)}>
                Показать документ
              </Button>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-2 flex items-end gap-2">
        <Field label="Причина доступа к документу" hint="Записывается в журнал аудита">
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Спор по заказу"
            className="w-64"
          />
        </Field>
      </div>

      {payload && (
        <pre className="mt-2 overflow-x-auto rounded-lg border border-neutral-200 p-2 text-[11px] dark:border-neutral-800">
          {JSON.stringify(payload, null, 1)}
        </pre>
      )}
    </div>
  );
}

/** Задачи из §14 идут по расписанию; здесь — ручной догон для эксплуатации. */
const JOBS: Array<{ id: string; label: string; hint: string }> = [
  { id: 'activate-rentals', label: 'Открыть аренды', hint: 'Доставленные с наступившей датой' },
  { id: 'mark-awaiting-return', label: 'Ожидание возврата', hint: 'За сутки до конца аренды' },
  { id: 'overdue-check', label: 'Пересчитать пеню', hint: 'По просроченным возвратам' },
  { id: 'return-reminders', label: 'Напоминания', hint: 'Возврат сегодня или завтра' },
  {
    id: 'expire-stale-payments',
    label: 'Закрыть зависшие платежи',
    hint: 'PENDING старше 12 часов',
  },
  { id: 'promo-expiry', label: 'Промокоды по сроку', hint: 'Деактивация истёкших' },
  { id: 'recheck-stale-holds', label: 'Сверить брони', hint: 'Удержания по закрытым заказам' },
  { id: 'cleanup', label: 'Уборка', hint: 'Старые коды и токены' },
];

function JobsPanel({
  onError,
  onDone,
}: {
  onError: (message: string | null) => void;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async (job: string) =>
      (await api.post<{ job: string; result: unknown }>(`/admin/orders/jobs/${job}/run`, {})).data,
    onSuccess: (data) => {
      onError(null);
      setResult(`${data.job}: ${JSON.stringify(data.result)}`);
      onDone();
    },
    onError: (e) => onError(errorMessage(e)),
  });

  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Фоновые задачи
      </Button>
    );
  }

  return (
    <Card
      title="Фоновые задачи"
      actions={
        <Button variant="ghost" onClick={() => setOpen(false)}>
          Свернуть
        </Button>
      }
    >
      <p className="mb-3 text-sm text-neutral-500">
        Идут по расписанию сами. Запуск вручную нужен, если задача упала или её надо догнать —
        повторный вызов ничего не портит, все задачи идемпотентны.
      </p>

      <div className="flex flex-wrap gap-2">
        {JOBS.map((job) => (
          <Button
            key={job.id}
            variant="secondary"
            disabled={run.isPending}
            title={job.hint}
            onClick={() => run.mutate(job.id)}
          >
            {job.label}
          </Button>
        ))}
      </div>

      {result && <p className="mt-3 font-mono text-xs text-neutral-500">{result}</p>}
    </Card>
  );
}
